// Pure subtitle editing. Invariants (mirrored by the server): cues are sorted, never overlap, last >= 100 ms, end <= project duration.
import type { AudioClip, Project, SubtitleCue, SubtitleStyle } from "../../../engine/src/types.ts";
import { uid } from "./ops.ts";

export const MIN_CUE_MS = 100;
export const MAX_CUES = 500;
export interface TranscriptCue { start_ms: number; end_ms: number; text: string }

const withCues = (p: Project, cues: SubtitleCue[]): Project => ({ ...p, subtitles: { ...p.subtitles, cues } });
const sorted = (cues: SubtitleCue[]) => [...cues].sort((a, b) => a.start_ms - b.start_ms);

/** Project-time cues for the audible part of a clip (trim and position are honoured). */
export function cuesFromTranscript(clip: AudioClip, transcript: TranscriptCue[]): SubtitleCue[] {
  const lo = clip.in_ms, hi = clip.in_ms + clip.duration_ms, out: SubtitleCue[] = [];
  for (const t of transcript) {
    const s = Math.max(t.start_ms, lo), e = Math.min(t.end_ms, hi);
    if (e - s < MIN_CUE_MS) continue;
    out.push({ id: uid("cue"), start_ms: clip.start_ms + (s - lo), end_ms: clip.start_ms + (e - lo), text: t.text.slice(0, 200) });
  }
  return out;
}

/** Insert cues, replacing any existing cue they overlap; clamps to the project and keeps the track valid. */
export function applyCues(p: Project, incoming: SubtitleCue[]): Project {
  const cues = incoming.map((c) => ({ ...c, start_ms: Math.max(0, c.start_ms), end_ms: Math.min(p.duration_ms, c.end_ms) })).filter((c) => c.end_ms - c.start_ms >= MIN_CUE_MS);
  if (!cues.length) return p;
  const kept = p.subtitles.cues.filter((o) => !cues.some((c) => o.start_ms < c.end_ms && c.start_ms < o.end_ms));
  const all = sorted([...kept, ...cues]).slice(0, MAX_CUES);
  const first = p.subtitles.cues.length === 0 && p.subtitles.style.font_size === 48;     // untouched default: fit the project's resolution
  const style = first ? { ...p.subtitles.style, font_size: Math.round(Math.min(p.width, p.height) * 0.05) } : p.subtitles.style;
  return { ...p, subtitles: { ...p.subtitles, enabled: true, style, cues: all } };
}

/** A new empty-ish cue at `startMs`, shortened to fit the gap before the next cue. */
export function addCue(p: Project, startMs: number, text = "…"): { project: Project; id: string } | null {
  if (p.subtitles.cues.length >= MAX_CUES) return null;
  const start = Math.max(0, Math.round(startMs));
  if (p.subtitles.cues.some((c) => start >= c.start_ms && start < c.end_ms)) return null;
  const next = sorted(p.subtitles.cues).find((c) => c.start_ms > start);
  const end = Math.min(p.duration_ms, next ? next.start_ms : p.duration_ms, start + 2000);
  if (end - start < MIN_CUE_MS) return null;
  const cue: SubtitleCue = { id: uid("cue"), start_ms: start, end_ms: end, text };
  return { project: applyCues(p, [cue]), id: cue.id };
}

export function updateCue(p: Project, id: string, patch: Partial<Pick<SubtitleCue, "start_ms" | "end_ms" | "text">>): Project {
  const list = sorted(p.subtitles.cues), i = list.findIndex((c) => c.id === id);
  if (i < 0) return p;
  const cur = list[i]!, prevEnd = i > 0 ? list[i - 1]!.end_ms : 0, nextStart = i < list.length - 1 ? list[i + 1]!.start_ms : p.duration_ms;
  let start = Math.round(patch.start_ms ?? cur.start_ms), end = Math.round(patch.end_ms ?? cur.end_ms);
  start = Math.min(Math.max(start, prevEnd), nextStart - MIN_CUE_MS);
  end = Math.max(Math.min(end, nextStart), start + MIN_CUE_MS);
  if (patch.start_ms !== undefined && patch.end_ms === undefined) end = Math.min(nextStart, Math.max(end, start + MIN_CUE_MS));
  const text = (patch.text ?? cur.text).slice(0, 200);
  if (!text.trim()) return p;
  if (start === cur.start_ms && end === cur.end_ms && text === cur.text) return p;
  return withCues(p, list.map((c) => (c.id === id ? { ...c, start_ms: start, end_ms: end, text } : c)));
}

/** Move a cue keeping its length, bounded by its neighbours. */
export function moveCue(p: Project, id: string, newStartMs: number): Project {
  const c = p.subtitles.cues.find((x) => x.id === id);
  if (!c) return p;
  const len = c.end_ms - c.start_ms, list = sorted(p.subtitles.cues).filter((x) => x.id !== id);
  const prev = [...list].reverse().find((x) => x.start_ms < c.start_ms), next = list.find((x) => x.start_ms > c.start_ms);
  const lo = prev ? prev.end_ms : 0, hi = (next ? next.start_ms : p.duration_ms) - len;
  const start = Math.min(Math.max(Math.round(newStartMs), lo), Math.max(lo, hi));
  return start === c.start_ms ? p : withCues(p, p.subtitles.cues.map((x) => (x.id === id ? { ...x, start_ms: start, end_ms: start + len } : x)));
}

export function removeCue(p: Project, id: string): Project {
  return p.subtitles.cues.some((c) => c.id === id) ? withCues(p, p.subtitles.cues.filter((c) => c.id !== id)) : p;
}
/** Style "one chip at a time": every cue becomes groups of `size` words. Without word timings from the speech engine the cue's time
 *  is shared in proportion to each group's length, which is close for narration but not exact — hence the cues stay editable. */
export function splitIntoWordGroups(p: Project, size = 2): Project {
  const out: SubtitleCue[] = [];
  for (const c of sorted(p.subtitles.cues)) {
    const words = c.text.trim().split(/\s+/).filter(Boolean), groups: string[] = [];
    for (let i = 0; i < words.length; i += size) groups.push(words.slice(i, i + size).join(" "));
    const total = groups.reduce((n, g) => n + g.length, 0), dur = c.end_ms - c.start_ms;
    if (groups.length < 2 || dur < groups.length * MIN_CUE_MS) { out.push(c); continue; }
    let at = c.start_ms, used = 0;
    groups.forEach((g, i) => {
      used += g.length; const end = i === groups.length - 1 ? c.end_ms : c.start_ms + Math.round(dur * used / total);
      out.push({ id: uid("cue"), start_ms: at, end_ms: Math.max(end, at + MIN_CUE_MS), text: g }); at = Math.max(end, at + MIN_CUE_MS);
    });
  }
  return out.length > MAX_CUES ? p : withCues(p, out);
}
export function clearCues(p: Project): Project { return p.subtitles.cues.length ? withCues(p, []) : p; }
export function setSubtitleStyle(p: Project, patch: Partial<SubtitleStyle>): Project {
  return { ...p, subtitles: { ...p.subtitles, style: { ...p.subtitles.style, ...patch } } };
}
export function setSubtitlesEnabled(p: Project, enabled: boolean): Project {
  return p.subtitles.enabled === enabled ? p : { ...p, subtitles: { ...p.subtitles, enabled } };
}
